import { useCallback, useReducer, useRef } from 'react'
import { useAuthContext } from '@/auth/AuthProvider'
import { apiFetch } from '@/api/client'
import type { Initiative } from '@/domain/types'

/**
 * Wire shape for `PATCH /api/initiatives/{id}`. All fields are optional —
 * the server merges with the current row. Set a nullable field to `null` to
 * clear it; omit to leave unchanged.
 *
 * Three things are deliberately absent:
 *
 *   - **Stage.** Sending `stageCode` (or `statusCode`, or `stageId`) gets a
 *     400 `STAGE_CHANGE_REQUIRES_GATE`. Stage moves only through the gate
 *     endpoint; leave PATCH able to set it and the whole funnel is
 *     bypassable in one call.
 *   - **Closure.** `closureReason` / `closedAt` are written by a Close or
 *     Salvage outcome at a gate, so the decision lands in the append-only
 *     log with a name against it. The server's schema is `.strict()` and
 *     rejects them.
 *   - **`forkedFromInitiativeId`.** Create-only. A fork is declared at
 *     birth; re-parenting one afterwards rewrites lineage that Gate 3's
 *     `governance_reconfirmed_or_fork` predicate reads.
 */
export interface UpdateInitiativeBody {
  initiativeName?: string
  initiativeDescription?: string
  businessUnitCode?: string
  capabilityId?: number
  ownerObjectId?: string

  blockedReasonCode?: string | null
  blockedNote?: string | null

  targetDate?: string | null

  successMeasure?: string | null
  baseline?: string | null
  baselineDeferred?: boolean
  dataRequirements?: string | null
  aiActClassCode?: string | null
  governanceNote?: string | null
  /**
   * Stamps `governanceNoteConfirmedAt` with the server's clock. There is no
   * raw-timestamp parameter on purpose — it would let a caller back-date
   * the re-confirmation Gate 3 tests against the Gate 1 clear, which is the
   * one thing that predicate exists to prevent.
   */
  confirmGovernanceNote?: boolean

  trackerUrl?: string | null
  pilotCohort?: string | null
  disclosureText?: string | null

  result?: string | null
  costEnvelope?: string | null
  runOwnerConfirmed?: boolean

  monitoringUrl?: string | null
  runbookUrl?: string | null
}

/**
 * Mutation hook for `PATCH /api/initiatives/{id}`. Like
 * `useDeleteInitiative`, the path varies per id — initiativeId is
 * supplied at mutate-time alongside the body.
 *
 * On success, the API returns the updated detail (200, full
 * `Initiative` shape). The hook's state transitions to `success` with
 * the data; callers route to the detail or close the modal.
 */
export type UpdateState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: Initiative }
  | { status: 'error'; error: Error }

export interface UseUpdateInitiativeResult {
  state: UpdateState
  mutate: (initiativeId: number, body: UpdateInitiativeBody) => Promise<Initiative | null>
  reset: () => void
}

type UpdateAction =
  | { type: 'FETCH' }
  | { type: 'SUCCESS'; data: Initiative }
  | { type: 'ERROR'; error: Error }
  | { type: 'RESET' }

function reducer(state: UpdateState, action: UpdateAction): UpdateState {
  switch (action.type) {
    case 'FETCH':
      return { status: 'loading' }
    case 'SUCCESS':
      return { status: 'success', data: action.data }
    case 'ERROR':
      return { status: 'error', error: action.error }
    case 'RESET':
      return { status: 'idle' }
    default:
      return state
  }
}

export function useUpdateInitiative(): UseUpdateInitiativeResult {
  const { getToken } = useAuthContext()
  const [state, dispatch] = useReducer(reducer, { status: 'idle' })

  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const seqRef = useRef(0)

  const mutate = useCallback(
    async (initiativeId: number, body: UpdateInitiativeBody): Promise<Initiative | null> => {
      const seq = ++seqRef.current
      dispatch({ type: 'FETCH' })
      try {
        const data = await apiFetch<Initiative>(
          `/api/initiatives/${initiativeId}`,
          () => getTokenRef.current(),
          {
            method: 'PATCH',
            body: JSON.stringify(body),
          },
        )
        if (seq !== seqRef.current) return null
        dispatch({ type: 'SUCCESS', data })
        return data
      } catch (err) {
        if (seq !== seqRef.current) return null
        dispatch({
          type: 'ERROR',
          error: err instanceof Error ? err : new Error(String(err)),
        })
        return null
      }
    },
    [],
  )

  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])

  return { state, mutate, reset }
}
